import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit3, Maximize2, Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight, Minus, Plus, Type, List, ListOrdered } from 'lucide-react';
import { Button } from './ui/button';
import { renderTemplateToImage, renderTemplateBackgroundOnly } from '../lib/templateRenderer';
import { generateEditableLayers } from '../lib/templateRenderer';
import { getTemplateForSlide } from '../data/themes';

/**
 * InlineSlideEditor - Click-to-edit text directly on the slide preview
 *
 * For template-sourced slides, text layers are rendered as absolutely-positioned
 * overlays on top of the slide image. Clicking a text region opens it for editing.
 * On commit, the template is re-rendered with updated content.
 *
 * Supports streaming text animation: when animateTextIn is true, shows background-only
 * image first, then fades in text layers one by one (Gamma-style).
 */
export default function InlineSlideEditor({
  slide,
  slideIndex,
  totalSlides,
  onSlideUpdate,
  onOpenFullEditor,
  theme,
  colorScheme,
  animateTextIn,
  navigationFade,
  onAnimationComplete,
}) {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [editingLayerId, setEditingLayerId] = useState(null);
  const [editText, setEditText] = useState('');
  const [hoveredLayerId, setHoveredLayerId] = useState(null);
  const [isRendering, setIsRendering] = useState(false);

  // Text style overrides per layer (persisted across edits)
  const [layerStyleOverrides, setLayerStyleOverrides] = useState({});

  const updateStyleOverride = useCallback((prop, value) => {
    if (!editingLayerId) return;
    setLayerStyleOverrides(prev => ({
      ...prev,
      [editingLayerId]: { ...(prev[editingLayerId] || {}), [prop]: value }
    }));
  }, [editingLayerId]);

  // Animation state: 'idle' | 'background' | 'animating' | 'complete'
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [visibleLayerCount, setVisibleLayerCount] = useState(0);
  const animationTimerRef = useRef(null);
  const startTimerRef = useRef(null);

  const canvasSize = slide?.extractedLayers?.canvas || { width: 1920, height: 1080 };

  // Track container size with ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Get text layers from extractedLayers
  const textLayers = (slide?.extractedLayers?.layers || []).filter(
    (l) => l.type === 'text' && l.visible !== false
  );

  // Get image layers (for selectable/movable images)
  const imageLayers = (slide?.extractedLayers?.layers || []).filter(
    (l) => l.type === 'image' && l.content && l.visible !== false
  );

  // Current editing layer's style overrides (must be after textLayers definition)
  const currentStyleOverride = editingLayerId ? (layerStyleOverrides[editingLayerId] || {}) : {};

  const scaleX = containerSize.width / canvasSize.width;
  const scaleY = containerSize.height / canvasSize.height;

  // Animation trigger — when animateTextIn becomes true for a new slide
  useEffect(() => {
    // Cleanup any existing timers
    if (animationTimerRef.current) clearInterval(animationTimerRef.current);
    if (startTimerRef.current) clearTimeout(startTimerRef.current);

    if (!animateTextIn || !slide?.extractedLayers?.backgroundOnly) {
      setAnimationPhase('idle');
      setVisibleLayerCount(textLayers.length);
      return;
    }

    // Phase 1: Show background only
    setAnimationPhase('background');
    setVisibleLayerCount(0);

    startTimerRef.current = setTimeout(() => {
      if (textLayers.length === 0) {
        // No text layers — go straight to complete
        setAnimationPhase('complete');
        onAnimationComplete?.();
        return;
      }

      setAnimationPhase('animating');

      // Phase 2: Reveal text layers one by one
      let count = 0;
      animationTimerRef.current = setInterval(() => {
        count++;
        setVisibleLayerCount(count);

        if (count >= textLayers.length) {
          clearInterval(animationTimerRef.current);
          animationTimerRef.current = null;

          // Phase 3: Brief pause then swap to full render
          setTimeout(() => {
            setAnimationPhase('complete');
            onAnimationComplete?.();
          }, 600);
        }
      }, 400);
    }, 300);

    return () => {
      if (animationTimerRef.current) clearInterval(animationTimerRef.current);
      if (startTimerRef.current) clearTimeout(startTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateTextIn, slide?.index]);

  // Navigation fade — simple fade-in on the full slide image (no per-layer animation)
  const [navFading, setNavFading] = useState(false);
  useEffect(() => {
    if (!navigationFade) {
      setNavFading(false);
      return;
    }
    setNavFading(true);
    const timer = setTimeout(() => {
      setNavFading(false);
      onAnimationComplete?.();
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationFade, slide?.index]);

  // Start editing a layer
  const handleLayerClick = useCallback((layer) => {
    // Block editing during animation
    if (animationPhase !== 'idle' && animationPhase !== 'complete') return;
    if (editingLayerId === layer.id) return;
    const text = layer.isTextEdited ? layer.editedText : layer.content;
    setEditingLayerId(layer.id);
    setEditText(text || '');
  }, [editingLayerId, animationPhase]);

  // Commit edit and re-render
  const handleCommit = useCallback(async () => {
    if (!editingLayerId) return;

    // If editing an image layer, just deselect — no text to commit
    const editingLayer = (slide.extractedLayers?.layers || []).find(l => l.id === editingLayerId);
    if (editingLayer?.type === 'image') {
      setEditingLayerId(null);
      return;
    }

    // Merge style overrides into layer
    const styleOverride = layerStyleOverrides[editingLayerId] || {};
    const updatedLayers = (slide.extractedLayers?.layers || []).map((l) =>
      l.id === editingLayerId
        ? {
            ...l,
            isTextEdited: true,
            editedText: editText,
            textStyle: { ...(l.textStyle || {}), ...styleOverride },
            styleOverrides: styleOverride,
          }
        : l
    );

    setEditingLayerId(null);
    setIsRendering(true);

    try {
      // Build content from updated layers
      const updatedContent = buildContentFromLayers(updatedLayers, slide);

      // Re-render template with updated content
      if (theme && colorScheme) {
        const template = getTemplateForSlide(
          { ...theme, colorScheme },
          { ...slide, ...updatedContent },
          slideIndex,
          totalSlides
        );

        // Apply style overrides to template elements before rendering
        // Note: template fontSize is in percentage of slide height (e.g. 4.5 = 4.5%)
        // Layer fontSize is in pixels (e.g. 48px). Convert pixel → percentage.
        const SLIDE_HEIGHT = 1080;
        const templateWithOverrides = { ...template };
        if (templateWithOverrides.layout?.elements) {
          templateWithOverrides.layout = {
            ...templateWithOverrides.layout,
            elements: templateWithOverrides.layout.elements.map(el => {
              const matchingLayer = updatedLayers.find(l => l.elementId === el.id);
              if (matchingLayer?.styleOverrides && Object.keys(matchingLayer.styleOverrides).length > 0) {
                const overrides = { ...matchingLayer.styleOverrides };
                // Convert pixel fontSize to percentage for the template renderer
                if (overrides.fontSize) {
                  overrides.fontSize = (overrides.fontSize / SLIDE_HEIGHT) * 100;
                }
                return { ...el, style: { ...(el.style || {}), ...overrides } };
              }
              return el;
            })
          };
        }

        const newPreview = await renderTemplateToImage(templateWithOverrides, updatedContent, colorScheme);
        const newBgOnly = await renderTemplateBackgroundOnly(templateWithOverrides, updatedContent, colorScheme);
        const newLayers = generateEditableLayers(templateWithOverrides, updatedContent, colorScheme);

        // Preserve edit state and style overrides on matching layers
        const mergedLayers = newLayers.map((nl) => {
          const prevLayer = updatedLayers.find((ul) => ul.elementId === nl.elementId);
          if (prevLayer?.isTextEdited) {
            return {
              ...nl,
              isTextEdited: true,
              editedText: prevLayer.editedText,
              styleOverrides: prevLayer.styleOverrides,
              textStyle: { ...(nl.textStyle || {}), ...(prevLayer.styleOverrides || {}) },
            };
          }
          return nl;
        });

        onSlideUpdate(slideIndex, {
          ...slide,
          title: updatedContent.title,
          content: updatedContent.content,
          bullet_points: updatedContent.bullet_points,
          preview_url: newPreview,
          extractedLayers: {
            ...slide.extractedLayers,
            background: newPreview,
            background_image: newPreview,
            backgroundOnly: newBgOnly,
            layers: mergedLayers,
          },
        });
      } else {
        // No theme available - just update layers without re-rendering
        onSlideUpdate(slideIndex, {
          ...slide,
          extractedLayers: {
            ...slide.extractedLayers,
            layers: updatedLayers,
          },
        });
      }
    } catch (err) {
      console.error('InlineSlideEditor: re-render failed', err);
    } finally {
      setIsRendering(false);
    }
  }, [editingLayerId, editText, slide, slideIndex, totalSlides, theme, colorScheme, onSlideUpdate]);

  // Cancel editing on Escape, Enter commits (Shift+Enter for newlines in bullet fields)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setEditingLayerId(null);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommit();
    }
  }, [editingLayerId, handleCommit]);

  // Click outside to commit
  const handleBackgroundClick = useCallback((e) => {
    if (editingLayerId && e.target === containerRef.current) {
      handleCommit();
    }
  }, [editingLayerId, handleCommit]);

  // Determine which image to show based on animation phase
  const isShowingBackground = !navFading && (animationPhase === 'background' || animationPhase === 'animating');
  const displayUrl = isShowingBackground
    ? (slide?.extractedLayers?.backgroundOnly || slide?.preview_url)
    : slide?.preview_url;

  const isAnimating = !navFading && (animationPhase === 'background' || animationPhase === 'animating');

  return (
    <div className="relative">
      {/* Toolbar — hide during animation */}
      {!isAnimating && (
        <div className="absolute top-2 right-2 z-20 flex gap-1.5">
          {onOpenFullEditor && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenFullEditor}
              className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm text-xs h-7 px-2 shadow-sm"
            >
              <Maximize2 className="w-3 h-3 mr-1" />
              Full Editor
            </Button>
          )}
        </div>
      )}

      {/* Rendering indicator */}
      {isRendering && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 rounded-lg">
          <div className="bg-white dark:bg-slate-800 rounded-lg px-3 py-1.5 text-xs font-medium shadow-lg">
            Updating...
          </div>
        </div>
      )}

      {/* Slide container with overlays */}
      <div
        ref={containerRef}
        className={`relative aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden cursor-default border-2 ${
          isAnimating ? 'border-slate-200 dark:border-slate-700' : 'border-green-500/30'
        }`}
        onClick={handleBackgroundClick}
      >
        {/* Background slide image */}
        {displayUrl && (
          <img
            key={navFading ? `nav-${slide?.index}` : undefined}
            src={displayUrl}
            alt={slide?.title || 'Slide'}
            className={`w-full h-full object-contain select-none pointer-events-none ${
              navFading ? 'animate-fade-in-up' : ''
            }`}
            style={navFading ? { animationDuration: '0.35s' } : undefined}
            draggable={false}
          />
        )}

        {/* Text layer overlays */}
        {containerSize.width > 0 && textLayers.map((layer, layerIndex) => {
          const isEditing = editingLayerId === layer.id;
          const isHovered = hoveredLayerId === layer.id;
          const lx = (layer.x || layer.bbox?.[0] || 0) * scaleX;
          const ly = (layer.y || layer.bbox?.[1] || 0) * scaleY;
          const lw = (layer.width || ((layer.bbox?.[2] || 0) - (layer.bbox?.[0] || 0))) * scaleX;
          const lh = (layer.height || ((layer.bbox?.[3] || 0) - (layer.bbox?.[1] || 0))) * scaleY;
          const scaledFontSize = (layer.textStyle?.fontSize || 24) * scaleY;

          const baseStyle = {
            position: 'absolute',
            left: lx,
            top: ly,
            width: lw,
            height: lh,
            fontSize: Math.max(scaledFontSize, 8),
            fontFamily: layer.textStyle?.fontFamily || 'Inter, sans-serif',
            fontWeight: layer.textStyle?.fontWeight || '400',
            textAlign: layer.textStyle?.textAlign || 'left',
            lineHeight: 1.2,
            overflow: 'hidden',
            boxSizing: 'border-box',
          };

          // Editing mode — show textarea with style toolbar
          if (isEditing) {
            const editColor = currentStyleOverride.color || layer.textStyle?.color || '#ffffff';
            const editFontSize = currentStyleOverride.fontSize || layer.textStyle?.fontSize || 24;
            const editFontWeight = currentStyleOverride.fontWeight || layer.textStyle?.fontWeight || '400';
            const editTextAlign = currentStyleOverride.textAlign || layer.textStyle?.textAlign || 'left';
            const editFontFamily = currentStyleOverride.fontFamily || layer.textStyle?.fontFamily || 'Inter, system-ui, sans-serif';
            const editFontStyle = currentStyleOverride.fontStyle || layer.textStyle?.fontStyle || 'normal';
            const editTextDecoration = currentStyleOverride.textDecoration || layer.textStyle?.textDecoration || 'none';
            const scaledEditFontSize = editFontSize * scaleY;
            // Detect dark vs light theme for textarea background
            const bgColor = colorScheme?.background || '#ffffff';
            const isDark = bgColor.match(/^#([0-9a-f]{2})/i)?.[1] ? parseInt(bgColor.match(/^#([0-9a-f]{2})/i)[1], 16) < 80 : false;
            const textareaBg = isDark ? 'rgba(15,23,42,0.94)' : 'rgba(255,255,255,0.94)';

            return (
              <React.Fragment key={layer.id}>
                {/* Text style toolbar */}
                <div
                  style={{
                    position: 'absolute',
                    left: Math.max(0, lx),
                    top: Math.max(0, ly - 36),
                    zIndex: 20,
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className="flex items-center gap-0.5 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-600 px-1.5 py-1"
                >
                  {/* Font family dropdown */}
                  <select
                    value={editFontFamily.split(',')[0].trim()}
                    onChange={(e) => updateStyleOverride('fontFamily', `${e.target.value}, system-ui, sans-serif`)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="text-[10px] bg-transparent border-none outline-none text-slate-600 dark:text-slate-300 cursor-pointer pr-0 max-w-[80px]"
                    title="Font family"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Playfair Display">Playfair</option>
                    <option value="Montserrat">Montserrat</option>
                    <option value="Roboto Mono">Roboto Mono</option>
                    <option value="Comic Neue">Comic Neue</option>
                  </select>

                  <div className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

                  {/* Font size controls */}
                  <button
                    onClick={() => updateStyleOverride('fontSize', Math.max(12, editFontSize - 2))}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-600 dark:text-slate-300"
                    title="Decrease font size"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 w-6 text-center font-mono select-none">{editFontSize}</span>
                  <button
                    onClick={() => updateStyleOverride('fontSize', Math.min(120, editFontSize + 2))}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-600 dark:text-slate-300"
                    title="Increase font size"
                  >
                    <Plus className="w-3 h-3" />
                  </button>

                  <div className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

                  {/* Bold toggle */}
                  <button
                    onClick={() => updateStyleOverride('fontWeight', editFontWeight === 'bold' || editFontWeight === '700' ? '400' : 'bold')}
                    className={`p-1 rounded ${editFontWeight === 'bold' || editFontWeight === '700' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Toggle bold"
                  >
                    <Bold className="w-3 h-3" />
                  </button>

                  {/* Italic toggle */}
                  <button
                    onClick={() => updateStyleOverride('fontStyle', editFontStyle === 'italic' ? 'normal' : 'italic')}
                    className={`p-1 rounded ${editFontStyle === 'italic' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Toggle italic"
                  >
                    <Italic className="w-3 h-3" />
                  </button>

                  {/* Underline toggle */}
                  <button
                    onClick={() => updateStyleOverride('textDecoration', editTextDecoration === 'underline' ? 'none' : 'underline')}
                    className={`p-1 rounded ${editTextDecoration === 'underline' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Toggle underline"
                  >
                    <Underline className="w-3 h-3" />
                  </button>

                  {/* Strikethrough toggle */}
                  <button
                    onClick={() => updateStyleOverride('textDecoration', editTextDecoration === 'line-through' ? 'none' : 'line-through')}
                    className={`p-1 rounded ${editTextDecoration === 'line-through' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Toggle strikethrough"
                  >
                    <Strikethrough className="w-3 h-3" />
                  </button>

                  <div className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

                  {/* Text alignment */}
                  <button
                    onClick={() => updateStyleOverride('textAlign', 'left')}
                    className={`p-1 rounded ${editTextAlign === 'left' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Align left"
                  >
                    <AlignLeft className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => updateStyleOverride('textAlign', 'center')}
                    className={`p-1 rounded ${editTextAlign === 'center' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Align center"
                  >
                    <AlignCenter className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => updateStyleOverride('textAlign', 'right')}
                    className={`p-1 rounded ${editTextAlign === 'right' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                    title="Align right"
                  >
                    <AlignRight className="w-3 h-3" />
                  </button>

                  <div className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

                  {/* Bullet list */}
                  <button
                    onClick={() => {
                      const lines = editText.split('\n');
                      const allBulleted = lines.every(l => l.startsWith('• ') || !l.trim());
                      const newText = allBulleted
                        ? lines.map(l => l.replace(/^• /, '')).join('\n')
                        : lines.map(l => l.trim() ? `• ${l.replace(/^(?:• |\d+\. )/, '')}` : l).join('\n');
                      setEditText(newText);
                    }}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-600 dark:text-slate-300"
                    title="Bullet list"
                  >
                    <List className="w-3 h-3" />
                  </button>

                  {/* Numbered list */}
                  <button
                    onClick={() => {
                      const lines = editText.split('\n');
                      const allNumbered = lines.every(l => /^\d+\. /.test(l) || !l.trim());
                      const newText = allNumbered
                        ? lines.map(l => l.replace(/^\d+\. /, '')).join('\n')
                        : lines.map((l, i) => l.trim() ? `${i + 1}. ${l.replace(/^(?:• |\d+\. )/, '')}` : l).join('\n');
                      setEditText(newText);
                    }}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-600 dark:text-slate-300"
                    title="Numbered list"
                  >
                    <ListOrdered className="w-3 h-3" />
                  </button>

                  <div className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

                  {/* Color picker */}
                  <label className="relative p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer" title="Text color">
                    <Type className="w-3 h-3" style={{ color: editColor }} />
                    <input
                      type="color"
                      value={editColor}
                      onChange={(e) => updateStyleOverride('color', e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </label>
                </div>

                {/* Textarea — uses semi-opaque background to hide canvas text beneath */}
                <textarea
                  style={{
                    ...baseStyle,
                    color: editColor,
                    fontSize: Math.max(scaledEditFontSize, 8),
                    fontWeight: editFontWeight,
                    fontStyle: editFontStyle,
                    textDecoration: editTextDecoration,
                    fontFamily: editFontFamily,
                    textAlign: editTextAlign,
                    background: textareaBg,
                    border: '2px solid rgba(34,197,94,0.6)',
                    borderRadius: '4px',
                    outline: 'none',
                    padding: '4px',
                    resize: 'none',
                    zIndex: 10,
                  }}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={handleCommit}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              </React.Fragment>
            );
          }

          // Animation mode — show visible text with fade-in
          if (isShowingBackground) {
            const isRevealed = layerIndex < visibleLayerCount;
            return (
              <div
                key={layer.id}
                style={{
                  ...baseStyle,
                  color: layer.textStyle?.color || '#1e293b',
                  opacity: isRevealed ? 1 : 0,
                  transform: isRevealed ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                  zIndex: 5,
                  pointerEvents: 'none',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {layer.content}
              </div>
            );
          }

          // Normal mode — transparent overlay for click-to-edit
          return (
            <div
              key={layer.id}
              style={{
                ...baseStyle,
                color: 'transparent',
                background: isHovered ? 'rgba(34,197,94,0.08)' : 'transparent',
                border: isHovered ? '2px dashed rgba(34,197,94,0.6)' : '2px solid transparent',
                cursor: 'pointer',
                zIndex: 5,
                transition: 'border-color 0.15s, background-color 0.15s',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleLayerClick(layer);
              }}
              onMouseEnter={() => setHoveredLayerId(layer.id)}
              onMouseLeave={() => setHoveredLayerId(null)}
              title={`Click to edit: ${layer.elementType || layer.type}`}
            />
          );
        })}

        {/* Image layer overlays — selectable, draggable images */}
        {containerSize.width > 0 && !isAnimating && imageLayers.map((layer) => {
          const lx = (layer.x || layer.bbox?.[0] || 0) * scaleX;
          const ly = (layer.y || layer.bbox?.[1] || 0) * scaleY;
          const lw = (layer.width || ((layer.bbox?.[2] || 0) - (layer.bbox?.[0] || 0))) * scaleX;
          const lh = (layer.height || ((layer.bbox?.[3] || 0) - (layer.bbox?.[1] || 0))) * scaleY;
          const isHovered = hoveredLayerId === layer.id;
          const isSelected = editingLayerId === layer.id;

          return (
            <div
              key={layer.id}
              style={{
                position: 'absolute',
                left: lx,
                top: ly,
                width: lw,
                height: lh,
                cursor: 'pointer',
                zIndex: 6,
                border: isSelected ? '2px solid rgba(59,130,246,0.9)' : isHovered ? '2px dashed rgba(59,130,246,0.6)' : '2px solid transparent',
                borderRadius: 8 * scaleX,
                background: isHovered && !isSelected ? 'rgba(59,130,246,0.05)' : 'transparent',
                transition: 'border-color 0.15s, background-color 0.15s',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setEditingLayerId(isSelected ? null : layer.id);
              }}
              onMouseEnter={() => setHoveredLayerId(layer.id)}
              onMouseLeave={() => setHoveredLayerId(null)}
              title="Click to select image"
            >
              {/* Transparent overlay — image is already in the canvas render */}
              {isSelected && (
                <>
                  {/* Corner resize handles */}
                  {['nw','ne','sw','se'].map(corner => (
                    <div key={corner} style={{
                      position: 'absolute',
                      width: 8, height: 8,
                      background: '#3b82f6',
                      border: '1px solid white',
                      borderRadius: 2,
                      ...(corner.includes('n') ? { top: -4 } : { bottom: -4 }),
                      ...(corner.includes('w') ? { left: -4 } : { right: -4 }),
                      cursor: `${corner}-resize`,
                      zIndex: 10,
                    }} />
                  ))}
                </>
              )}
            </div>
          );
        })}

        {/* Edit mode indicator — hide during animation */}
        {!isAnimating && !editingLayerId && textLayers.length > 0 && (
          <div className="absolute bottom-2 left-2 z-10 bg-green-600/80 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 backdrop-blur-sm">
            <Edit3 className="w-2.5 h-2.5" />
            Click text to edit
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Build content object from edited layers, mapping back to slide fields
 */
function buildContentFromLayers(layers, originalSlide) {
  const content = {
    title: originalSlide.title || '',
    content: originalSlide.content || '',
    bullet_points: originalSlide.bullet_points || [],
    bulletPoints: originalSlide.bullet_points || [],
    _slideImageUrl: originalSlide._slideImageUrl || null,
  };

  for (const layer of layers) {
    if (!layer.isTextEdited || !layer.editedText) continue;

    const etype = layer.elementType || layer.elementId;
    if (etype === 'title' || etype === 'big-statement') {
      content.title = layer.editedText;
    } else if (etype === 'content' || etype === 'subtitle') {
      content.content = layer.editedText;
    } else if (
      etype === 'bullets' ||
      etype === 'icon-bullets' ||
      etype === 'numbered-list' ||
      etype === 'feature-showcase' ||
      etype === 'metrics-dashboard' ||
      etype === 'icon-grid' ||
      etype === 'cards' ||
      etype === 'three-column'
    ) {
      // Parse bullet text back into array
      content.bullet_points = layer.editedText
        .split('\n')
        .map((l) => l.replace(/^[•\-\d.)\s]+/, '').trim())
        .filter(Boolean);
      content.bulletPoints = content.bullet_points;
    }
  }

  return content;
}
